/**
 * Initialize all ACF Google Maps on the page
 */
export const acfMaps = () => {
    if (typeof google === 'undefined') {
        console.warn('ACF Maps: Google Maps API not loaded yet.');
        return;
    }

    const maps = document.querySelectorAll('.acf-map');
    if (!maps.length) return;

    maps.forEach(el => {
        const markers = el.querySelectorAll('.marker');
        const viewport = el.closest('.map-viewport');
        const markerLabel = viewport ? viewport.querySelector('.map-marker-label') : null;
        const radiusCircle = viewport ? viewport.querySelector('.radius-circle') : null;

        const dataZoom = parseFloat(el.getAttribute('data-zoom'));
        const args = {
            zoom: !isNaN(dataZoom) ? dataZoom : 13,
            center: new google.maps.LatLng(0, 0),
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            scrollwheel: false,
            disableDefaultUI: true,
            gestureHandling: 'greedy',
            zoomControl: false,
            disableDoubleClickZoom: true,
            styles: [
                { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#999999' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
                { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d6d6d6' }] },
                { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#aaaaaa' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
                { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
                { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ededed' }] },
                { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#d5d5d5' }] },
                { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
                { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
                { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
                { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cccccc' }] },
            ]
        };

        const map = new google.maps.Map(el, args);
        const bounds = new google.maps.LatLngBounds();
        let hasCoords = false;
        let markerLatLng = null;

        markers.forEach(markerEl => {
            const lat = parseFloat(markerEl.getAttribute('data-lat'));
            const lng = parseFloat(markerEl.getAttribute('data-lng'));

            if (isNaN(lat) || isNaN(lng)) return;

            const coords = new google.maps.LatLng(lat, lng);
            bounds.extend(coords);
            hasCoords = true;
            markerLatLng = coords;
        });

        if (hasCoords) {
            if (markers.length === 1) {
                const center = bounds.getCenter();
                // Shift center south so the pin sits in the upper portion of the map
                const offsetCenter = new google.maps.LatLng(center.lat() - 0.012, center.lng());
                map.setCenter(offsetCenter);
                map.setZoom(args.zoom);
            } else {
                map.fitBounds(bounds);
            }
        }

        // Shared flag: true while a landmark is focused, hides property overlays
        let landmarkFocused = false;

        // Keep .map-marker-label and .radius-circle anchored to the lat/lng on drag/zoom.
        // Hidden while a landmark is focused so they don't jump off-screen.
        if (markerLatLng && (markerLabel || radiusCircle)) {
            const updateOverlays = () => {
                if (landmarkFocused) return;

                const projection = map.getProjection();
                const mapBounds = map.getBounds();
                if (!projection || !mapBounds) return;

                const worldPoint = projection.fromLatLngToPoint(markerLatLng);
                const ne = projection.fromLatLngToPoint(mapBounds.getNorthEast());
                const sw = projection.fromLatLngToPoint(mapBounds.getSouthWest());

                const xPx = (worldPoint.x - sw.x) / (ne.x - sw.x) * el.offsetWidth;
                const yPx = (worldPoint.y - ne.y) / (sw.y - ne.y) * el.offsetHeight;

                if (radiusCircle) {
                    radiusCircle.style.left = xPx + 'px';
                    radiusCircle.style.top = yPx + 'px';
                    radiusCircle.style.transform = 'translate(-50%, -50%)';
                }
                if (markerLabel) {
                    markerLabel.style.left = xPx + 'px';
                    markerLabel.style.top = yPx + 'px';
                    markerLabel.style.transform = 'translate(-50%, calc(-100% - 10px))';
                }
            };

            map.addListener('bounds_changed', updateOverlays);
            map.addListener('idle', updateOverlays);
        }

        const setOverlaysVisible = (visible) => {
            const v = visible ? '' : 'hidden';
            if (markerLabel) markerLabel.style.visibility = v;
            if (radiusCircle) radiusCircle.style.visibility = v;
        };

        // Add landmark pins with a back button to restore the property view.
        if (viewport) {
            const themePath = document.querySelector('link[href*="themes/reportage"]')?.href?.split('/wp-content')[0] || '';
            const pinUrl = themePath + '/wp-content/themes/reportage/src/img/mappin.svg';
            const defaultIcon = {
                url: pinUrl,
                scaledSize: new google.maps.Size(23, 40),
                anchor: new google.maps.Point(9, 40),
            };
            const activeIcon = {
                url: pinUrl,
                scaledSize: new google.maps.Size(30, 52),
                anchor: new google.maps.Point(12, 52),
            };

            const originalCenter = markerLatLng;
            const originalZoom = args.zoom;
            let activePin = null;
            let activeCard = null;

            // Back button — injected into the map viewport
            const backBtn = document.createElement('button');
            backBtn.className = 'map-back-btn';
            backBtn.innerHTML = '← Back to property';
            backBtn.style.display = 'none';
            viewport.appendChild(backBtn);

            const resetToProperty = () => {
                if (activePin) activePin.setIcon(defaultIcon);
                if (activeCard) activeCard.classList.remove('landmark-active');
                tooltip.close();
                activePin = null;
                activeCard = null;
                landmarkFocused = false;
                backBtn.style.display = 'none';
                setOverlaysVisible(true);
                map.panTo(originalCenter);
                map.setZoom(originalZoom);
            };

            backBtn.addEventListener('click', resetToProperty);

            // Shared InfoWindow for landmark tooltips
            const tooltip = new google.maps.InfoWindow({
                pixelOffset: new google.maps.Size(0, 5),
                disableAutoPan: true,
            });

            // Build pin groups keyed by data-min — hidden until scroll reaches that distance
            const pinGroups = {};

            viewport.querySelectorAll('.landmark-group').forEach(group => {
                const groupIndex = parseInt(group.dataset.min);
                const pins = [];

                group.querySelectorAll('.landmark-card[data-lat]').forEach(card => {
                    const lat = parseFloat(card.dataset.lat);
                    const lng = parseFloat(card.dataset.lng);
                    const label = card.textContent.trim();

                    if (isNaN(lat) || isNaN(lng)) return;

                    const position = new google.maps.LatLng(lat, lng);
                    const pin = new google.maps.Marker({
                        position,
                        map: null,
                        title: label,
                        icon: defaultIcon,
                    });

                    const focusLandmark = () => {
                        if (activePin) activePin.setIcon(defaultIcon);
                        if (activeCard) activeCard.classList.remove('landmark-active');

                        pin.setIcon(activeIcon);
                        card.classList.add('landmark-active');
                        landmarkFocused = true;
                        setOverlaysVisible(false);
                        backBtn.style.display = 'flex';
                        map.panTo(position);
                        map.setZoom(16);

                        tooltip.setContent(`<div style="font-size:12px;font-weight:600;padding:1px 6px;white-space:nowrap;">${label}</div>`);
                        tooltip.open(map, pin);

                        activePin = pin;
                        activeCard = card;
                    };

                    card.addEventListener('mouseenter', () => {
                        if (pin.getMap()) pin.setAnimation(google.maps.Animation.BOUNCE);
                    });
                    card.addEventListener('mouseleave', () => {
                        pin.setAnimation(null);
                    });

                    card.style.cursor = 'pointer';
                    card.addEventListener('click', focusLandmark);
                    pin.addListener('click', focusLandmark);
                    pins.push(pin);
                });

                if (pins.length) pinGroups[groupIndex] = pins;
            });

            const fadePin = (pin, show) => {
                if (show) {
                    pin.setMap(map);
                    pin.setIcon(pin === activePin ? activeIcon : defaultIcon);
                } else {
                    pin.setMap(null);
                }
            };

            // Show/hide pin groups as the scroll radius reaches each distance step
            window.addEventListener('landmark-group-change', (e) => {
                const activeIndex = e.detail.activeIndex;
                Object.entries(pinGroups).forEach(([index, pins]) => {
                    const visible = parseInt(index) === activeIndex;
                    pins.forEach((pin, i) => {
                        if (visible) {
                            setTimeout(() => fadePin(pin, true), i * 100);
                        } else {
                            setTimeout(() => fadePin(pin, false), i * 60);
                        }
                    });
                });
            });
        }
    });
};
